/**
 * ASSET PERFORMANCE CHART - THEME-AWARE VISUALIZATION
 *
 * THEME-AWARE DESIGN:
 * ==================
 * This component uses CSS custom properties for all chart colors,
 * ensuring seamless adaptation to light/dark theme changes.
 *
 * Color Variables Used:
 * - --chart-primary: Main chart color (area fill and stroke)
 * - --chart-grid: Grid lines and axis lines
 * - --chart-axis: Axis labels and tick marks
 * - --chart-tooltip-bg: Tooltip background
 * - --chart-tooltip-border: Tooltip border
 * - --chart-tooltip-text: Tooltip text color
 *
 * Benefits:
 * - Automatic theme adaptation (light/dark mode)
 * - Consistent with application design system
 * - No hardcoded colors in component code
 * - Centralized color management through CSS variables
 */

import { useEffect, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { cn } from '../../lib/utils';
import { services } from '../../services';

interface AssetPerformanceChartProps {
  className?: string;
}

interface ChartDataPoint {
  month: string;
  value: number;
  yield: number;
}

const formatValue = (value: number) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  return `$${(value / 1000).toFixed(0)}K`;
};

export function AssetPerformanceChart({ className }: AssetPerformanceChartProps) {
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAssetPerformanceData();
  }, []);

  const fetchAssetPerformanceData = async () => {
    try {
      setLoading(true);
      const response = await services.get<{
        status: boolean;
        message?: string;
        data: Array<{
          month: string;
          value: number;
          yield: number;
        }>;
      }>('/admin/dashboard/asset-performance');

      if (response.status === 200 && response.data?.status && response.data?.data) {
        setChartData(response.data.data);
      } else if (response.status === 401) {
        console.error('Authentication required for asset performance data');
        setChartData([]);
      } else if (response.status === 403) {
        console.error('Admin access required for asset performance data');
        setChartData([]);
      } else {
        // Fallback to empty data if API fails
        setChartData([]);
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 401) {
        console.error('Authentication required for asset performance data');
        setChartData([]);
      } else if (axiosError?.response?.status === 403) {
        console.error('Admin access required for asset performance data');
        setChartData([]);
      } else {
        // Fallback to empty data
        console.error('Error fetching asset performance data:', error);
        setChartData([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Default data if no data available
  const displayData = chartData.length > 0 ? chartData : [];

  return (
    <div className={cn('glass-card rounded-xl p-6 animate-slide-up', className)}>
      <div className='flex items-center justify-between mb-6'>
        <div>
          <h3 className='text-lg font-semibold text-foreground'>Asset Performance</h3>
          <p className='text-sm text-muted-foreground'>Total AUM over time</p>
        </div>
        <div className='flex gap-4 text-sm'>
          <div className='flex items-center gap-2'>
            <span
              className='w-3 h-3 rounded-full'
              style={{
                backgroundColor: `hsl(var(--chart-primary))`,
              }}
            />
            <span className='text-muted-foreground'>AUM</span>
          </div>
        </div>
      </div>
      <div className='h-[280px]'>
        {loading ? (
          <div className='flex items-center justify-center h-full text-muted-foreground'>
            <p>Loading chart data...</p>
          </div>
        ) : !ResponsiveContainer ? (
          <div className='flex items-center justify-center h-full text-muted-foreground'>
            <p>Chart library not installed. Please install recharts.</p>
          </div>
        ) : displayData.length === 0 ? (
          <div className='flex items-center justify-center h-full text-muted-foreground'>
            <p>No performance data available</p>
          </div>
        ) : (
          <ResponsiveContainer width='100%' height='100%'>
            <AreaChart data={displayData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id='colorValue' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor={`hsl(var(--chart-primary))`} stopOpacity={0.3} />
                  <stop offset='95%' stopColor={`hsl(var(--chart-primary))`} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray='3 3'
                stroke={`hsl(var(--chart-grid))`}
                vertical={false}
              />
              <XAxis
                dataKey='month'
                tick={{
                  fill: `hsl(var(--chart-axis))`,
                  fontSize: 12,
                }}
                axisLine={{
                  stroke: `hsl(var(--chart-grid))`,
                }}
                tickLine={false}
              />
              <YAxis
                tickFormatter={formatValue}
                tick={{
                  fill: `hsl(var(--chart-axis))`,
                  fontSize: 12,
                }}
                axisLine={{
                  stroke: `hsl(var(--chart-grid))`,
                }}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: `hsl(var(--chart-tooltip-bg))`,
                  border: `1px solid hsl(var(--chart-tooltip-border))`,
                  borderRadius: '8px',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
                }}
                labelStyle={{
                  color: `hsl(var(--chart-tooltip-text))`,
                }}
                formatter={(value: number) => [formatValue(value), 'AUM']}
              />
              <Area
                type='monotone'
                dataKey='value'
                stroke={`hsl(var(--chart-primary))`}
                strokeWidth={2}
                fillOpacity={1}
                fill='url(#colorValue)'
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
